import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';
import Page from '../../Page';
import Breadcrumb from '../../Breadcrumb';

/**
 * What visitors should know before they drive out — dogs, picnics, tours, parking.
 *
 * These are the questions people actually ask, and none of them are crawlable,
 * which is why coverage sat at ~4.5% of listings: nobody had ever been asked.
 *
 * Saves through `listing-profile-answers` with no activities, so it writes the
 * amenity rows and stamps their confirmation without re-asking what happens at
 * the winery — and without touching the categories.
 */
export default function AmenitiesEditForm() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const role = user?.role === 'admin' ? 'admin' : 'industry';

  const [amenities, setAmenities] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.service(`${role}/accommodation`).find({
          query: { $limit: 200, $sort: { type: 1, name: 1 } },
        });
        if (!cancelled) setAmenities(res.data || res || []);
      } catch (e) {
        console.log('could not load amenities', e);
      }
    })();
    return () => { cancelled = true; };
  }, [role]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let row = listing?.id === lid ? listing : null;
      if (!row) {
        try {
          row = await client.service(`${role}/listing`).get(lid);
          if (cancelled) return;
          dispatch(setListing(row));
        } catch (e) {
          console.log('could not load listing', e);
          return;
        }
      }
      setChosen(new Set((row.accommodations || []).map((a) => a.id)));
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, role, dispatch]);

  if (!listing || listing.id !== lid) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.service('industry/listing-profile-answers').create({
        listingId: lid,
        activities: {},
        amenities: [...chosen],
      });
      toast.success('Saved');
      navigate(`/${role}/listings/${lid}`);
    } catch (err) {
      toast.error(err.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const grouped = amenities.reduce((acc, a) => {
    (acc[a.type || 'other'] = acc[a.type || 'other'] || []).push(a);
    return acc;
  }, {});

  return (
    <Page>
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Listing Detail', href: `/${role}/listings/${lid}/` },
              { name: `${listing.name}: What visitors should know` },
            ]}
          />
        </nav>
      </div>

      <form onSubmit={save} className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            What visitors should know
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            The things people ask before they set off. None of it is anywhere we
            can find it — only you know.
          </p>
        </div>

        <div className="mt-5 md:col-span-2 md:mt-0 space-y-5">
          {Object.entries(grouped).map(([type, list]) => (
            <div key={type}>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">{type}</div>
              <div className="flex flex-wrap gap-2">
                {list.map((a) => {
                  const on = chosen.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        const next = new Set(chosen);
                        if (on) next.delete(a.id); else next.add(a.id);
                        setChosen(next);
                      }}
                      className={`px-3 py-1 text-sm rounded-full border ${
                        on
                          ? 'bg-red-800 border-red-800 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {amenities.length === 0 && (
            <p className="text-sm text-gray-500">Nothing to choose from yet.</p>
          )}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-gray-200 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(`/${role}/listings/${lid}`)}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      </form>
    </Page>
  );
}
